/**
 * Single-flight async chain with a trailing rerun and a per-iteration gate —
 * the mechanism behind _mountHomeV2 (main.js delegates here; extracted so the
 * legacy-flip interleaving is unit-testable, same dirty-chain shape as
 * queue-state.js).
 *
 * Why the gate runs EVERY iteration and not just at request time: the
 * homepage-layout preference can flip to legacy while a mount is in flight
 * (the Settings 0102 PATCH → _applyHomepageLayoutChange tears v2 down and
 * bumps _menuDataGen via _loadMenuData). A queued trailing rerun re-reads the
 * CURRENT gen when it finally executes, so the gen guards inside
 * _mountHomeV2Inner pass and the rerun would recreate the 13 v2 sections over
 * the legacy chrome (P1: home-your-shelves "legacy layout round-trip"
 * journey). The gen mechanism cannot catch this; only rechecking the gate at
 * the top of each iteration can.
 */
export function createSingleFlightChain({ gate = () => true, run }) {
  let inFlight = null;
  let dirty = false;
  return {
    /** Run now, or queue exactly one trailing rerun if already running. */
    request() {
      if (inFlight) {
        dirty = true;
        return inFlight;
      }
      inFlight = (async () => {
        do {
          dirty = false;
          // `continue` re-evaluates the while(dirty) condition, so a request
          // queued while the gate was closed still gets its iteration — and
          // that iteration rechecks the gate in turn (no one-way latch).
          if (!gate()) continue;
          try {
            await run();
          } catch { /* run() renders its own errors; never wedge the chain */ }
        } while (dirty);
      })().finally(() => { inFlight = null; });
      return inFlight;
    },
  };
}
